import { useEffect, useRef, useState } from "react";
import moment from "moment-timezone";
import { ChatMessageList } from "@/components/chat-view";
import type { ChatViewMessage, ChatViewPagination } from "@/components/chat-view";
import { useGoToMessages } from "@/lib/api/gotoChat";
import type { GoToMessageWithRelations } from "@/lib/api/gotoChat";
import {
  GoToChatFilters,
  buildGoToParams,
} from "./GoToChatFilters";
import type { GoToChatFilterValues } from "./GoToChatFilters";

const CommonDestinationTimezone = "America/Chicago";

// ── Default filters: last 7 days, "all" ────────────────────────────────────

function defaultFilterValues(): GoToChatFilterValues {
  return {
    filter_type: "by_number",
    date_from: moment.tz(CommonDestinationTimezone).subtract(7, "days").startOf("day").toISOString(),
    date_to: moment.tz(CommonDestinationTimezone).endOf("day").toISOString(),
    pageSize: 50,
    phone_number: "",
    number_a: "",
    number_b: "",
    owner_number: "",
    counterparty_number: "",
    entity_type: "user",
    entity_id: "",
    side: "any",
  };
}

// ── Map API message → generic ChatViewMessage ───────────────────────────────

/**
 * NOTE: Direction alignment decision —
 * We always show inbound messages on the left and outbound on the right,
 * based on the `direction` field from the API. This works well for
 * conversation / owner_conversation filters but may be less intuitive for
 * "all" or "by_entity" views where messages span many parties. This can
 * be revisited to allow the user to designate an "owner side" if needed.
 */
function mapToViewMessage(msg: GoToMessageWithRelations): ChatViewMessage {
  // Build secondary label: entity name if present on the sender side
  let secondaryLabel: string | undefined;
  if (msg.FromUser) {
    secondaryLabel = msg.FromUser.name;
  } else if (msg.FromCustomer) {
    secondaryLabel = msg.FromCustomer.long_name;
  }

  return {
    id: msg.id,
    side: msg.direction === "outbound" ? "right" : "left",
    label: msg.from_number,
    secondaryLabel,
    body: msg.body,
    imageUrl: msg.document_id && msg.presigned_get_url ? msg.presigned_get_url : null,
    timestamp: msg.message_timestamp,
  };
}

// ── Component ───────────────────────────────────────────────────────────────

export function GoToChatAuditDashboard() {
  const [filters, setFilters] = useState<GoToChatFilterValues>(defaultFilterValues);
  const [appliedFilters, setAppliedFilters] = useState<GoToChatFilterValues>(defaultFilterValues);
  const [page, setPage] = useState(1);
  const [resetKey, setResetKey] = useState(0);

  // Accumulated messages across all loaded pages for the current filter set.
  // Stored as raw API messages so we only map to view messages at render time.
  const [accumulatedMessages, setAccumulatedMessages] = useState<GoToMessageWithRelations[]>([]);
  // Track which page+filter payloads we've already folded in so we don't
  // double-append on re-renders or cross-filter transitions.
  const incorporatedPages = useRef<Set<string>>(new Set());

  const params = buildGoToParams(appliedFilters, page);
  const queryResult = useGoToMessages(params);
  const filterSignature = JSON.stringify(appliedFilters);

  const responseData = queryResult?.data;
  const isSuccess = responseData && "success" in responseData && responseData.success;
  const messagesResponse = isSuccess
    ? (responseData as { success: true; data: { data: GoToMessageWithRelations[]; pagination: { currentPage: number; totalPages: number; totalItems: number; pageSize: number } } }).data
    : null;

  const rawMessages: GoToMessageWithRelations[] = messagesResponse?.data || [];
  const apiPagination = messagesResponse?.pagination || {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 50,
  };

  // When new page data arrives, merge it into the accumulated list.
  // API returns newest-first (DESC). Higher page numbers = older messages.
  // We prepend older pages so the accumulated list stays in DESC order,
  // then reverse the whole thing at render time for chronological display.
  useEffect(() => {
    if (rawMessages.length === 0) return;

    const incorporationKey = `${filterSignature}|page:${apiPagination.currentPage}`;
    if (incorporatedPages.current.has(incorporationKey)) return;

    incorporatedPages.current.add(incorporationKey);

    if (apiPagination.currentPage === 1) {
      // First page (newest) — replace everything
      const sortedPage = rawMessages.sort(
        (a, b) => new Date(a.message_timestamp).getTime() - new Date(b.message_timestamp).getTime()
      );
      setAccumulatedMessages(sortedPage);
    } else {
      // Older page — prepend and sort once on update.
      setAccumulatedMessages((prev) => {
        const merged = [...rawMessages, ...prev];
        merged.sort((a, b) => new Date(a.message_timestamp).getTime() - new Date(b.message_timestamp).getTime());
        return merged;
      });
    }
  }, [rawMessages, apiPagination.currentPage, filterSignature]);

  // Build the view list in chronological order (oldest first)
  const chronologicalMessages = accumulatedMessages;
  const viewMessages: ChatViewMessage[] = chronologicalMessages.map(mapToViewMessage);

  const viewPagination: ChatViewPagination = {
    currentPage: apiPagination.currentPage,
    totalPages: apiPagination.totalPages,
    totalItems: apiPagination.totalItems,
    pageSize: apiPagination.pageSize,
  };

  const handleApply = () => {
    // Reset everything for the new filter set
    setAccumulatedMessages([]);
    incorporatedPages.current = new Set<string>();
    setAppliedFilters({ ...filters });
    setPage(1);
    setResetKey((prev) => prev + 1);
  };

  const handleLoadOlder = () => {
    if (queryResult.isFetching) return;
    setPage((prev) => prev + 1);
  };

  const hasOlderMessages = apiPagination.currentPage < apiPagination.totalPages;

  return (
    <div className="flex flex-col h-screen max-h-[calc(100vh-6rem)]">
      {/* Header — mobile filter button sits here via md:hidden on the Sheet trigger */}
      <div className="flex items-center justify-between mb-2">
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          GoTo Connect Messages
        </h1>
      </div>

      {/*
        GoToChatFilters renders two things:
        1. A Sheet trigger button (md:hidden) — appears inline here, visible only on mobile
        2. A desktop sidebar div (hidden md:flex) — appears inline here, visible only on md+

        We wrap both the filters and the chat area in a flex row.
        On mobile the sidebar div is hidden so only the chat fills the row,
        and the Sheet button appears above in a small bar.
      */}
      <div className="flex flex-1 gap-4 min-h-0">
        <GoToChatFilters
          values={filters}
          onChange={setFilters}
          onApply={handleApply}
        />

        {/* Chat area */}
        <div className="flex flex-col flex-1 border rounded-lg overflow-hidden min-h-0">
          <ChatMessageList
            messages={viewMessages}
            pagination={viewPagination}
            totalLoaded={accumulatedMessages.length}
            isLoading={queryResult.isFetching}
            hasOlderMessages={hasOlderMessages}
            onLoadOlder={handleLoadOlder}
            resetKey={resetKey}
          />
        </div>
      </div>
    </div>
  );
}
